import React, { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';

// Layouts
import UserLayout from './layouts/UserLayout';
import PublicLayout from './layouts/PublicLayout';

// Error handling
import RouteError from './components/RouteError';

// Main pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import Pricing from './pages/Pricing';
import FAQ from './pages/FAQ';
import Demo from './pages/Demo';
import Dashboard from './pages/Dashboard';
import Contacts from './pages/Contacts';
import Emails from './pages/Emails';
import Analytics from './pages/Analytics';
import GroupAnalytics from './pages/admin/GroupAnalytics';
import DmarcOverview from './pages/admin/Dmarc/Overview';
import DmarcDomainDetail from './pages/admin/Dmarc/DomainDetail';
import DmarcReportDetail from './pages/admin/Dmarc/ReportDetail';
import DmarcSources from './pages/admin/Dmarc/Sources';
import TlsRptOverview from './pages/admin/TlsRpt/Overview';
import TlsRptDomainDetail from './pages/admin/TlsRpt/DomainDetail';
import TlsRptReportDetail from './pages/admin/TlsRpt/ReportDetail';
import ForwardMail from './pages/admin/ForwardMail';
import Help from './pages/Help';
import TrainingContentPage from './pages/TrainingContentPage';
import PublicHelp from './pages/PublicHelp';
import Settings from './pages/Settings';
import Calendar from './pages/Calendar';
import Welcome from './pages/Welcome';
import BounceDetail from './pages/BounceDetail.tsx';
import ComplaintDetail from './pages/ComplaintDetail.tsx';
import SupplierManagement from './pages/SupplierManagement';
import AuditTrail from './pages/AuditTrail';

import Tags from './pages/admin/Tags';
import ConnectInstances from './pages/admin/ConnectInstances';
import ConnectInstanceDetail from './pages/admin/ConnectInstanceDetail';


// Admin pages
import Users from './pages/admin/Users';
import MailMerge from './pages/admin/MailMerge';
import Invitations from './pages/admin/Invitations';
import ReleaseNotes from './pages/admin/ReleaseNotes';
import Clients from './pages/admin/Clients';
import ClientsEdit from './pages/admin/ClientsEdit';
import GroupManagement from './pages/admin/GroupManagement';
import QrCodes from './pages/admin/QrCodes';
import DomainFinder from './pages/admin/DomainFinder';
import EmailSchedule from './pages/admin/EmailSchedule';

// Router options for future flags
const routerOptions = {
  basename: import.meta.env.BASE_URL,
};

/**
 * Application router configuration
 * Note: This is a factory function to avoid HMR issues
 */
export const createAppRouter = () => createBrowserRouter([
  {
    // Public landing page as root
    path: '/',
    element: <PublicLayout />,
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        element: <Landing />,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'signup',
        element: <Signup />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        path: 'accept-invite',
        element: <AcceptInvite />,
      },
      {
        path: 'pricing',
        element: <Pricing />,
      },
      {
        path: 'faq',
        element: <FAQ />,
      },
      {
        path: 'demo',
        element: <Demo />,
      },
      {
        path: 'help',
        element: <PublicHelp />,
      },
    ],
  },
  {
    // Protected routes under /app
    path: '/app',
    element: <UserLayout />,
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      {
        path: 'dashboard',
        element: <Dashboard />,
      },
      {
        path: 'welcome',
        element: <Welcome />,
      },
      {
        path: 'contacts',
        element: <Contacts />,
      },
      {
        path: 'contacts/bounces/:id',
        element: <BounceDetail />,
      },
      {
        path: 'contacts/complaints/:id',
        element: <ComplaintDetail />,
      },
      {
        path: 'emails',
        element: <Emails />,
      },
      {
        path: 'analytics',
        element: <Analytics />,
      },
      {
        path: 'help',
        element: <Help />,
      },
      {
        path: 'help/training/:id',
        element: <TrainingContentPage />,
      },
      {
        path: 'agency-settings',
        element: <Settings />,
      },
      {
        path: 'calendar',
        element: <Calendar />,
      },
      // Admin routes
      {
        path: 'admin',
        children: [
          {
            path: 'client-users',
            element: <Users />,
          },
          {
            path: 'mail-merge',
            element: <MailMerge />,
          },
          {
            path: 'invitations',
            element: <Invitations />,
          },
          {
            path: 'release-notes',
            element: <ReleaseNotes />,
          },
          {
            path: 'clients',
            element: <Clients />,
          },
          {
            path: 'clients/:id',
            element: <ClientsEdit />,
          },


          {
            path: 'tags',
            element: <Tags />,
          },
          {
            path: 'groups',
            element: <GroupManagement />,
          },
          {
            path: 'audit-trail',
            element: <AuditTrail />,
          },
          {
            path: 'connect-instances',
            element: <ConnectInstances />,
          },
          {
            path: 'connect-instances/:id',
            element: <ConnectInstanceDetail />,
          },
          {
            path: 'group-analytics',
            element: <GroupAnalytics />,
          },
          {
            path: 'dmarc',
            element: <DmarcOverview />,
          },
          {
            path: 'dmarc/sources',
            element: <DmarcSources />,
          },
          {
            path: 'dmarc/domains/:domain',
            element: <DmarcDomainDetail />,
          },
          {
            path: 'dmarc/domains/:domain/reports/:reportUid',
            element: <DmarcReportDetail />,
          },
          {
            path: 'tlsrpt',
            element: <TlsRptOverview />,
          },
          {
            path: 'tlsrpt/domains/:domain',
            element: <TlsRptDomainDetail />,
          },
          {
            path: 'tlsrpt/domains/:domain/reports/:reportUid',
            element: <TlsRptReportDetail />,
          },
          {
            path: 'forward-mail',
            element: <ForwardMail />,
          },
          {
            path: 'qrcodes',
            element: <QrCodes />,
          },
          {
            path: 'domain-finder',
            element: <DomainFinder />,
          },
          {
            path: 'email-schedule',
            element: <EmailSchedule />,
          },
        ],
      },
    ],
  },
], routerOptions);

// Default export for backward compatibility (but use createAppRouter in App.tsx)
const router = createAppRouter();
export default router;
